import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus, X } from "lucide-react";
import type { AdminAreaView, AdminGovernorateView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { PageHeaderActions } from "@/shared/layout/PageHeaderActions";
import {
  Button,
  ErrorState,
  Icon,
  LoadingState,
  SearchInput,
  Select,
  StatTile,
  TableEmpty,
  TablePagination,
  Tabs,
  TileGrid,
  type TabItem,
} from "@/shared/rd";
import { AREA_ROW_COLUMNS, AreaTableRow } from "../components/AreaTableRow";
import { AreaEditor } from "../components/AreaEditor";
import { useAreasQuery } from "../services/useAreasQuery";
import { useAreaGovernoratesQuery } from "../services/useAreaGovernoratesQuery";

type Props = {
  locale: string;
};

type AreaTab = "all" | "active" | "paused";

const ALL = "";
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

function govName(gov: AdminGovernorateView, locale: string): string {
  return (locale === "ar" ? gov.nameAr : (gov.nameEn ?? gov.nameAr)) || gov.nameAr;
}

export function AreasPage({ locale }: Props) {
  const t = useTranslations();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [queryInput, setQueryInput] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [governorateFilter, setGovernorateFilter] = useState<string>(ALL);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<AreaTab>("all");
  // Collapsed rather than expanded: the design opens every governorate, and a
  // set of what is SHUT stays correct when the page's governorates change.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(queryInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [queryInput]);

  // A filter change can shrink the result set below the current page —
  // always land back on page 1 when the search term or governorate changes.
  // Adjusted during render (not an effect) per React's "adjusting state when
  // a prop changes" pattern.
  const [appliedFilters, setAppliedFilters] = useState({
    q: debouncedQuery,
    governorateId: governorateFilter,
  });
  if (appliedFilters.q !== debouncedQuery || appliedFilters.governorateId !== governorateFilter) {
    setAppliedFilters({ q: debouncedQuery, governorateId: governorateFilter });
    setPage(1);
  }

  const { data, isLoading, error, refetch } = useAreasQuery({
    governorateId: governorateFilter === ALL ? undefined : governorateFilter,
    q: debouncedQuery || undefined,
    page,
    limit: PAGE_SIZE,
  });
  const { data: governorates } = useAreaGovernoratesQuery();

  const govById = useMemo(() => {
    const map = new Map<string, AdminGovernorateView>();
    for (const gov of governorates ?? []) map.set(gov.id, gov);
    return map;
  }, [governorates]);

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // Deleting the last row of the last page shrinks the result set under the
  // current page. Clamp during render; guarded on `!isLoading` because
  // mid-fetch `total` is 0.
  if (!isLoading && page > totalPages) {
    setPage(totalPages);
  }
  const isFiltered = Boolean(debouncedQuery) || governorateFilter !== ALL;
  // The tiles describe the page in hand, not the catalogue: the list is paged
  // server-side, so a split across every page would need an endpoint for it.
  const activeCount = items.filter((area) => area.isActive).length;
  const pausedCount = items.length - activeCount;

  // Grouping only spans the current page — a governorate's rows can split
  // across a page boundary, which is expected with server-side pagination.
  const sections = useMemo(() => {
    const map = new Map<string, AdminAreaView[]>();
    for (const area of items) {
      // The tab filters what is already on the page; it is not a server filter.
      if (tab === "active" && !area.isActive) continue;
      if (tab === "paused" && area.isActive) continue;
      const arr = map.get(area.governorateId) ?? [];
      arr.push(area);
      map.set(area.governorateId, arr);
    }
    return [...map.entries()]
      .map(([governorateId, groupItems]) => ({
        governorateId,
        title: govById.has(governorateId) ? govName(govById.get(governorateId)!, locale) : governorateId,
        items: groupItems,
      }))
      .sort((a, b) => a.title.localeCompare(b.title, locale));
  }, [items, govById, locale, tab]);

  const tabItems: TabItem<AreaTab>[] = [
    { value: "all", label: t(keys.areas.tabAll) },
    { value: "active", label: t(keys.areas.tabActive) },
    { value: "paused", label: t(keys.areas.tabPaused) },
  ];

  const governorateOptions = (governorates ?? [])
    .map((gov) => ({ value: gov.id, label: govName(gov, locale) }))
    .sort((a, b) => a.label.localeCompare(b.label, locale));

  const header = (
    <PageHeaderActions>
      <Button variant="primary" size="sm" icon={Plus} onClick={() => setIsAddOpen(true)}>
        {t(keys.areas.addAction)}
      </Button>
    </PageHeaderActions>
  );

  const pageName = t("areas.pageName");
  if (isLoading && !data) {
    return (
      <>
        {header}
        <LoadingState title={t(keys.shell.loadingTitle, { page: pageName })} sub={t(keys.shell.loadingSub)} />
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        {header}
        <ErrorState
          title={t(keys.shell.errorTitle, { page: pageName })}
          sub={t(keys.shell.errorSub)}
          retryLabel={t(keys.shell.retry)}
          onRetry={() => void refetch()}
        />
      </>
    );
  }

  const clearFilters = () => {
    setQueryInput("");
    setDebouncedQuery("");
    setGovernorateFilter(ALL);
  };
  // What the empty table says depends on why it is empty: a search or
  // governorate that matched nothing, a tab with nothing on this page, or a
  // catalogue with no areas at all.
  const governorateLabel = governorateFilter !== ALL && govById.has(governorateFilter)
    ? govName(govById.get(governorateFilter)!, locale)
    : "";
  const emptyView = isFiltered ? (
    <TableEmpty
      illustration="admin-no-results"
      title={
        debouncedQuery && governorateLabel
          ? t("areas.emptyMatchBoth", { query: debouncedQuery, governorate: governorateLabel })
          : debouncedQuery
            ? t("areas.emptyMatchSearch", { query: debouncedQuery })
            : t("areas.emptyMatchGovernorate", { governorate: governorateLabel || governorateFilter })
      }
      actions={
        <Button variant="secondary" size="sm" icon={X} onClick={clearFilters}>
          {t(keys.common.clearFilters)}
        </Button>
      }
    >
      {t("areas.emptyFilteredSub")}
    </TableEmpty>
  ) : items.length > 0 ? (
    <TableEmpty
      illustration="admin-no-results"
      title={t(tab === "active" ? "areas.emptyActiveTitle" : "areas.emptyPausedTitle")}
      actions={
        <Button variant="secondary" size="sm" icon={X} onClick={() => setTab("all")}>
          {t("areas.emptyShowAll")}
        </Button>
      }
    >
      {t("areas.emptyTabSub")}
    </TableEmpty>
  ) : (
    <TableEmpty
      illustration="admin-empty-table"
      title={t("areas.emptyTitle")}
      actions={
        <Button variant="primary" size="sm" icon={Plus} onClick={() => setIsAddOpen(true)}>
          {t(keys.areas.addAction)}
        </Button>
      }
    >
      {t("areas.emptySub")}
    </TableEmpty>
  );

  return (
    <>
      {header}

      <TileGrid min={200}>
        <StatTile badge="map" value={total.toLocaleString(locale === "ar" ? "ar-EG" : "en-US")} label={t(keys.areas.statTotal)} />
        <StatTile badge="pin" value={activeCount.toLocaleString(locale === "ar" ? "ar-EG" : "en-US")} label={t(keys.areas.statActive)} />
        <StatTile badge="clock" value={pausedCount.toLocaleString(locale === "ar" ? "ar-EG" : "en-US")} label={t(keys.areas.statPaused)} />
      </TileGrid>

      <Tabs tabs={tabItems} value={tab} onChange={setTab} label={t(keys.areas.title)} />

      <div className="rd-toolbar">
        <SearchInput
          value={queryInput}
          onChange={(event) => setQueryInput(event.target.value)}
          placeholder={t(keys.areas.searchPlaceholder)}
          aria-label={t(keys.areas.searchToggle)}
          style={{ width: 260, maxWidth: "100%" }}
        />
        <Select
          size="sm"
          value={governorateFilter}
          onChange={(event) => setGovernorateFilter(event.target.value)}
          aria-label={t(keys.areas.governorateLabel)}
          placeholder={t(keys.areas.allGovernorates)}
          options={governorateOptions}
          style={{ width: 200, maxWidth: "100%" }}
        />
      </div>


      <div className="rd-table" role="table" aria-label={t(keys.areas.title)}>
        <div className="rd-table-row rd-table-head" role="row" style={{ gridTemplateColumns: AREA_ROW_COLUMNS }}>
          <div className="rd-cell" role="columnheader">
            {t(keys.areas.colArea)}
          </div>
          <div className="rd-cell" role="columnheader">
            {t(keys.areas.colStatus)}
          </div>
          <div className="rd-cell" role="columnheader" />
        </div>

        {sections.length === 0 ? (
          emptyView
        ) : (
          sections.map((section) => {
            const isOpen = !collapsed.has(section.governorateId);
            return (
              <div key={section.governorateId} role="rowgroup">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  title={t(keys.areas.groupToggle)}
                  onClick={() =>
                    setCollapsed((shut) => {
                      const next = new Set(shut);
                      if (!next.delete(section.governorateId)) next.add(section.governorateId);
                      return next;
                    })
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    height: 36,
                    padding: "0 20px",
                    border: 0,
                    borderBottom: "1px solid var(--rd-line-header)",
                    background: "var(--rd-surface-sunken)",
                    font: "inherit",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--rd-text)",
                    textAlign: "start",
                  }}
                >
                  <Icon icon={isOpen ? ChevronDown : ChevronRight} size={14} className={isOpen ? undefined : "rd-flip-rtl"} />
                  <span style={{ flex: 1 }}>{section.title}</span>
                  <span style={{ fontWeight: 400, color: "var(--rd-text-tertiary)" }}>
                    {t("areas.groupCount", { count: section.items.length })}
                  </span>
                </button>
                {isOpen
                  ? section.items.map((area) => <AreaTableRow key={area.id} area={area} />)
                  : null}
              </div>
            );
          })
        )}

        {total > 0 ? (
          <TablePagination
            total={total}
            page={page}
            pageSize={PAGE_SIZE}
            pageSizes={null}
            onPage={setPage}
            locale={locale}
            labels={{
              range: (from, to, count) => t(keys.common.paginationSummary, { from, to, total: count }),
              rows: t("areas.paginationRows"),
              rowsPerPage: t("areas.paginationRowsPerPage"),
              previous: t(keys.common.previous),
              next: t(keys.common.next),
              pages: t(keys.common.paginationNav),
            }}
          />
        ) : null}
      </div>

      {isAddOpen ? <AreaEditor onClose={() => setIsAddOpen(false)} /> : null}
    </>
  );
}
